import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaNewspaper,
  FaPlus,
  FaEdit,
  FaTrash,
  FaStar,
  FaRegStar,
  FaSync,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Blog {
  id: number;
  title: string;
  tags: string;
  publishDate: string;
  authorName: string;
  isHighlighted: boolean;
  excerpt: string;
  viewCount: number;
  language: string;
  languageDisplay: string;
}

// ==================== MODAL TYPES ====================
type ModalType = "confirm" | "success" | "error";

interface ModalState {
  isOpen: boolean;
  type: ModalType;
  title: string;
  message: string;
  blogTitle: string;
  onConfirm: (() => void) | null;
}

const MODAL_INITIAL: ModalState = {
  isOpen: false,
  type: "confirm",
  title: "",
  message: "",
  blogTitle: "",
  onConfirm: null,
};

// ==================== MODAL COMPONENT ====================
const Modal = ({
  modal,
  onClose,
}: {
  modal: ModalState;
  onClose: () => void;
}) => {
  if (!modal.isOpen) return null;

  const isConfirm = modal.type === "confirm";
  const isSuccess = modal.type === "success";

  // Color scheme per type
  const colors = {
    confirm: {
      bar: "bg-red-500",
      iconBg: "bg-red-50",
      icon: "text-red-500",
      titleText: "text-red-700",
      highlight: "bg-red-50 border border-red-200",
      highlightLabel: "text-red-400",
      highlightTitle: "text-red-800",
    },
    success: {
      bar: "bg-green-500",
      iconBg: "bg-green-50",
      icon: "text-green-500",
      titleText: "text-green-700",
      highlight: "bg-green-50 border border-green-200",
      highlightLabel: "text-green-500",
      highlightTitle: "text-green-800",
    },
    error: {
      bar: "bg-orange-500",
      iconBg: "bg-orange-50",
      icon: "text-orange-500",
      titleText: "text-orange-700",
      highlight: "",
      highlightLabel: "",
      highlightTitle: "",
    },
  };

  const c = colors[modal.type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={isConfirm ? undefined : onClose}
      />

      {/* Modal Card */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Top accent bar */}
        <div className={`h-1 ${c.bar}`} />

        <div className="p-6">
          {/* Icon row */}
          <div className="flex items-start gap-4 mb-4">
            <div className={`p-3 rounded-full flex-shrink-0 ${c.iconBg}`}>
              {isConfirm && (
                <FaExclamationTriangle className={c.icon} size={20} />
              )}
              {isSuccess && <FaCheckCircle className={c.icon} size={20} />}
              {modal.type === "error" && (
                <FaExclamationTriangle className={c.icon} size={20} />
              )}
            </div>

            <div className="pt-0.5">
              <h3 className={`text-base font-bold ${c.titleText}`}>
                {modal.title}
              </h3>
              <p className="text-gray-500 text-sm mt-0.5">{modal.message}</p>
            </div>

            {/* Close X (only for success/error) */}
            {!isConfirm && (
              <button
                onClick={onClose}
                className="ml-auto text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0"
              >
                <FaTimes size={14} />
              </button>
            )}
          </div>

          {/* Blog title box (confirm + success only) */}
          {modal.blogTitle && (isConfirm || isSuccess) && (
            <div className={`rounded-lg px-4 py-3 mb-4 ${c.highlight}`}>
              <p
                className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${c.highlightLabel}`}
              >
                Blog Post
              </p>
              <p
                className={`font-semibold text-sm truncate ${c.highlightTitle}`}
              >
                {modal.blogTitle}
              </p>
            </div>
          )}

          {/* Warning note (confirm only) */}
          {isConfirm && (
            <p className="text-xs text-gray-400 mb-5">
              This action is permanent and cannot be undone. The blog post and
              its associated image will be removed.
            </p>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 justify-end">
            {isConfirm ? (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    modal.onConfirm?.();
                    onClose();
                  }}
                  className="px-4 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <FaTrash size={12} /> Delete Blog
                </button>
              </>
            ) : (
              <button
                onClick={onClose}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors ${
                  isSuccess
                    ? "bg-green-500 hover:bg-green-600"
                    : "bg-orange-500 hover:bg-orange-600"
                }`}
              >
                OK
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const AdminBlogManagement = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [modal, setModal] = useState<ModalState>(MODAL_INITIAL);
  const navigate = useNavigate();

  // ---- Modal helpers ----
  const closeModal = () => setModal(MODAL_INITIAL);

  const showConfirm = (blogId: number, blogTitle: string) => {
    setModal({
      isOpen: true,
      type: "confirm",
      title: "Delete Blog Post",
      message: "Are you sure you want to delete this blog post?",
      blogTitle,
      onConfirm: () => confirmDelete(blogId, blogTitle),
    });
  };

  const showSuccess = (blogTitle: string) => {
    setModal({
      isOpen: true,
      type: "success",
      title: "Blog Deleted",
      message: "The blog post has been successfully removed.",
      blogTitle,
      onConfirm: null,
    });
  };

  const showError = (message: string) => {
    setModal({
      isOpen: true,
      type: "error",
      title: "Delete Failed",
      message,
      blogTitle: "",
      onConfirm: null,
    });
  };

  // ---- Data ----
  const fetchBlogs = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/blogs`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch blogs");
      setBlogs(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // ---- Delete ----
  const handleDelete = (id: number, title: string) => {
    showConfirm(id, title);
  };

  const confirmDelete = async (id: number, title: string) => {
    setDeleteLoading(id);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/blogs/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        let msg = `Server error (Status: ${response.status})`;
        try {
          const txt = await response.text();
          if (txt) msg = txt;
        } catch {}
        throw new Error(msg);
      }

      await fetchBlogs();
      showSuccess(title);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      console.error("❌ Delete error:", err);
      setError(`Failed to delete "${title}": ${msg}`);
      showError(msg);
    } finally {
      setDeleteLoading(null);
    }
  };

  // ---- Highlight ----
  const toggleHighlight = async (id: number) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/blogs/${id}/highlight`,
        { method: "PATCH", credentials: "include" },
      );
      if (!response.ok) throw new Error("Failed to toggle highlight");
      fetchBlogs();
    } catch (err) {
      showError(
        "Failed to toggle highlight: " +
          (err instanceof Error ? err.message : "Unknown error"),
      );
    }
  };

  // ---- Helpers ----
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Modal */}
      <Modal modal={modal} onClose={closeModal} />

      {/* Header */}
      <PageHeader
        icon={<FaNewspaper />}
        title="Manage Blog Posts"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Blogs" },
        ]}
      />

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-red-700">Error</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
              <button
                onClick={() => setError("")}
                className="text-red-400 hover:text-red-600"
              >
                <FaTimes size={14} />
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#009fe3] tracking-tight">
            Blog Posts ({blogs.length})
          </h2>
          <Link
            to="/admin/create-blog"
            className="bg-[#009fe3] hover:bg-[#007cb5] text-white font-semibold px-5 py-2 rounded-lg shadow transition-colors text-base w-full sm:w-auto text-center flex items-center justify-center gap-2"
          >
            <FaPlus /> Create New Blog
          </Link>
        </div>

        {/* Loading */}
        {loading && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
            <p className="text-gray-600">Loading blogs...</p>
          </div>
        )}

        {/* Fetch error (no blogs loaded) */}
        {!loading && error && blogs.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
            <button
              onClick={fetchBlogs}
              className="w-full bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        {!loading && (blogs.length > 0 || !error) && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {blogs.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-500 text-lg mb-4">No blog posts yet.</p>
                <Link
                  to="/admin/create-blog"
                  className="inline-flex items-center gap-2 bg-[#009fe3] hover:bg-[#007cb5] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  <FaPlus /> Create Your First Blog
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[#009fe3] text-white">
                    <tr>
                      {[
                        "ID",
                        "Title",
                        "Language",
                        "Tags",
                        "Author",
                        "Views",
                        "Publish Date",
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                        Highlighted
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {blogs.map((blog) => (
                      <tr
                        key={blog.id}
                        className="hover:bg-[#e0f7ff]/40 transition"
                      >
                        <td className="px-4 py-3 font-medium text-sm">
                          {blog.id}
                        </td>

                        <td className="px-4 py-3">
                          <a
                            href={`/blog/${blog.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-bold text-[#009fe3] hover:text-[#007cb5] hover:underline text-sm"
                          >
                            {blog.title}
                          </a>
                          {blog.excerpt && (
                            <p className="text-xs text-gray-400 mt-0.5 max-w-md truncate">
                              {blog.excerpt}
                            </p>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          <span className="inline-block bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold">
                            {blog.languageDisplay}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-600">
                          {blog.tags || (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-700">
                          {blog.authorName}
                        </td>

                        <td className="px-4 py-3 text-sm font-semibold text-gray-700">
                          {blog.viewCount.toLocaleString()}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(blog.publishDate)}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => toggleHighlight(blog.id)}
                            className={`p-2 rounded-full transition-colors ${
                              blog.isHighlighted
                                ? "bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                                : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                            }`}
                            title={
                              blog.isHighlighted
                                ? "Remove highlight"
                                : "Add highlight"
                            }
                          >
                            {blog.isHighlighted ? <FaStar /> : <FaRegStar />}
                          </button>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() =>
                                navigate(`/admin/edit-blog/${blog.id}`)
                              }
                              className="bg-[#009fe3] hover:bg-[#007cb5] text-white px-4 py-1 rounded-lg font-semibold shadow transition-colors flex items-center gap-1 text-sm"
                            >
                              <FaEdit /> Edit
                            </button>
                            <button
                              onClick={() => handleDelete(blog.id, blog.title)}
                              disabled={deleteLoading === blog.id}
                              className="bg-red-100 hover:bg-red-200 text-red-600 px-4 py-1 rounded-lg font-semibold shadow transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {deleteLoading === blog.id ? (
                                <>
                                  <FaSync className="animate-spin" />{" "}
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <FaTrash /> Delete
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBlogManagement;
